// Libraries
import { Visibility } from '@makemydeal/dr-sales-checkin-types';
import type { CapabilityConfig, FeatureName, SubFeatureOf } from '@makemydeal/dr-sales-checkin-types';

// Types
import type { SalesCheckinRootState } from '../common/types/SalesCheckinRootState';
import type { CapabilitiesReducerState } from './types';
import { getCapabilitiesInitialState } from './initialState';

/**
 * Frozen so the fail-closed fallback returns a *stable* reference: building a fresh
 * `{ visibility: HIDDEN }` on every call would hand `useSelector` a new identity each
 * time and re-render forever.
 */
const HIDDEN: CapabilityConfig = Object.freeze({ visibility: Visibility.HIDDEN });

/** Raw slice, defaulted to `{}`. Rarely consumed directly — prefer the gates below. */
export const getCapabilitiesState = (state: SalesCheckinRootState): CapabilitiesReducerState =>
    state.salesCheckin?.capabilities ?? getCapabilitiesInitialState();

/**
 * The config for one feature. Fail-closed: an unknown feature is HIDDEN, returned as
 * the stable `HIDDEN` reference.
 */
export const selectCapability =
    <F extends FeatureName>(feature: F) =>
    (state: SalesCheckinRootState): CapabilityConfig =>
        getCapabilitiesState(state)[feature] ?? HIDDEN;

/**
 * The single visibility gate shared by every enforcement level — route guard,
 * navigation, and feature level. FULL and PARTIAL are both visible; only HIDDEN is not.
 *
 */
export const isFeatureVisible =
    <F extends FeatureName>(feature: F) =>
    (state: SalesCheckinRootState): boolean =>
        selectCapability(feature)(state).visibility !== Visibility.HIDDEN;

/**
 * Tri-state resolver — the *only* place anything reasons about subfeatures, so no
 * consumer has to branch on `visibility` itself.
 *
 * FULL -> true, HIDDEN -> false, PARTIAL -> whatever `subFeatures` says (absent -> false).
 *
 */
export const canUseSubFeature =
    <F extends FeatureName>(feature: F, sub: SubFeatureOf<F>) =>
    (state: SalesCheckinRootState): boolean => {
        const capability = selectCapability(feature)(state);

        return (
            capability.visibility === Visibility.FULL ||
            (capability.visibility === Visibility.PARTIAL && !!capability.subFeatures[sub as string])
        );
    };
